import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtEUR, fmtDate, QUOTE_STATUS } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/Layout";
import { DocumentDialog } from "../components/DocumentDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Download, Pencil, Trash2, Search, FileCheck, ArrowRightLeft } from "lucide-react";
import { generateDocumentPDF } from "../lib/pdf";
import { toast } from "sonner";

export default function Quotes() {
  const qc = useQueryClient();
  const { data: quotes = [] } = useQuery({ queryKey: ["quotes"], queryFn: api.listQuotes });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["quotes"] }); qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const filtered = quotes.filter((i) =>
    (i.number + " " + (i.client?.name || "")).toLowerCase().includes(search.toLowerCase())
  );

  const handleConvert = async (q) => {
    const inv = await api.convertQuote(q.id);
    refresh();
    toast.success(`Devis converti en ${inv.number}`);
  };

  const confirmDelete = async () => {
    await api.deleteQuote(toDelete.id);
    setToDelete(null);
    refresh();
    toast.success("Devis supprimé");
  };

  return (
    <div>
      <PageHeader
        title="Devis"
        subtitle={`${quotes.length} devis`}
        action={
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="create-quote-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Créer un devis
          </Button>
        }
      />

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9 bg-white" placeholder="Rechercher un devis..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="quote-search-input" />
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="px-4 py-3 font-semibold">N° Devis</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Émission</th>
                <th className="px-4 py-3 font-semibold">Validité</th>
                <th className="px-4 py-3 font-semibold text-right">TTC</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length ? filtered.map((q) => (
                <tr key={q.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`quote-row-${q.number}`}>
                  <td className="px-4 py-3 font-mono text-slate-700">{q.number}</td>
                  <td className="px-4 py-3 font-medium">{q.client?.name}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(q.date_emission)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(q.date_echeance)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmtEUR(q.total_ttc)}</td>
                  <td className="px-4 py-3"><StatusBadge meta={QUOTE_STATUS[q.status]} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleConvert(q)} title="Transformer en facture" data-testid={`convert-quote-${q.number}`}><ArrowRightLeft className="h-4 w-4 text-[#C5A059]" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { generateDocumentPDF(q, settings || {}, "quote"); toast.success(`PDF ${q.number} téléchargé`); }} title="Télécharger PDF" data-testid={`download-quote-${q.number}`}><Download className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(q); setOpen(true); }} title="Modifier" data-testid={`edit-quote-${q.number}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setToDelete(q)} title="Supprimer" data-testid={`delete-quote-${q.number}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                  <FileCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />Aucun devis
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DocumentDialog
        open={open} onOpenChange={setOpen} type="quote" clients={clients}
        editingDoc={editing} defaultTva={settings?.default_tva || 10}
        apiCreate={api.createQuote} apiUpdate={api.updateQuote} onSaved={refresh}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le devis ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible. {toDelete?.number} sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-quote">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
